import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FlagCircleIcon from '@mui/icons-material/FlagCircle';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { VirtualTree, type VirtualTreeApiRef } from 'components/tree/VirtualTree';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import { GlobalContext } from 'GlobalContext';
import { useAssignPositionQuery, type EmployeeOrgpath } from './hooks/AssignPosition/useAssignPositionQuery';
import { useAssignPositionForm, type PositionRow, type AreaTreeNode, type WorkPositionTreeNode } from '../employee/hooks/AssignPosition/useAssignPositionForm';
import { useAssignPositionActions } from './hooks/AssignPosition/useAssignPositionActions';

interface AssignPositionNewProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    reloading: () => void;
    data?: {
        employee_id: number;
        org_path?: EmployeeOrgpath[];
        hire_date?: EmployeeOrgpath[];
    };
}

// Card por asignación: edición de fechas inline + botón para abrir el modal de selección de puesto.
const AssignmentCard = ({
    row,
    index,
    onRemove,
    onRestore,
    onToggleEdit,
    onOpenOrgPicker,
    onChangeDate,
    contractStart,
    contractEnd,
    dateErrors,
}: {
    row: PositionRow;
    index: number;
    onRemove: (id: string) => void;
    onRestore: (id: string) => void;
    onToggleEdit: (id: string) => void;
    onOpenOrgPicker: (id: string) => void;
    onChangeDate: (id: string, field: 'start_date' | 'end_date', value: any) => void;
    contractStart: Dayjs | null;
    contractEnd: Dayjs | null;
    dateErrors: string[];
}) => {
    const { t } = useTranslation();

    return (
        <Card variant="outlined" sx={{ mb: 1.5, ...(dateErrors.length > 0 && { borderColor: 'error.main' }) }}>
            <CardContent sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
                    <Chip label={`${t('timekeeper.employees.dialog.assign_position.assignment', 'Asignación')} ${index + 1}`} color="default" size="small" variant="outlined" />
                    {row.to_delete && <Chip label={t('timekeeper.employees.dialog.assign_position.to_delete', 'Se eliminará')} color="error" size="small" />}
                </Stack>
                <Stack spacing={0.25} sx={{ mb: row.editing ? 1.5 : 0 }}>
                    <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
                        {row.work_position_name || t('timekeeper.employees.dialog.assign_position.position', 'Puesto')}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ width: 'fit-content' }}>
                        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            <Typography variant="body2" color="text.secondary" noWrap>
                                {row.subcompany_name}
                            </Typography>
                            {row.area_path_names.map((name, i) => (
                                <Typography key={i} variant="body2" color={i === row.area_path_names.length - 1 ? 'text.primary' : 'text.secondary'} noWrap>
                                    {name}
                                </Typography>
                            ))}
                        </Breadcrumbs>
                        <IconButton color="inherit" onClick={() => onOpenOrgPicker(row.local_id)}>
                            <EditOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Stack>
                    {!row.editing && (row.start_date || row.end_date) && (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="subtitle2" color="text.secondary">
                                {row.start_date?.format('DD/MM/YYYY') ?? '—'} → {row.end_date?.format('DD/MM/YYYY') ?? '—'}
                            </Typography>
                            <IconButton color="inherit" onClick={() => onToggleEdit(row.local_id)}>
                                <EditCalendarIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Stack>
                    )}
                </Stack>

                {row.editing && (
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                        <Grid container alignItems="center">
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <DatePicker
                                    label={t('timekeeper.employees.dialog.assign_position.start_date', 'Inicio')}
                                    value={row.start_date}
                                    onChange={(val) => onChangeDate(row.local_id, 'start_date', val)}
                                    format="DD/MM/YYYY"
                                    formatDensity="spacious"
                                    minDate={contractStart ?? undefined}
                                    maxDate={contractEnd ?? undefined}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 1 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="body2">a</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <DatePicker
                                    label={t('timekeeper.employees.dialog.assign_position.end_date', 'Fin')}
                                    value={row.end_date}
                                    onChange={(val) => onChangeDate(row.local_id, 'end_date', val)}
                                    format="DD/MM/YYYY"
                                    formatDensity="spacious"
                                    minDate={row.start_date ?? contractStart ?? undefined}
                                    maxDate={contractEnd ?? undefined}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                            </Grid>
                            {row.editing && (
                                <Grid size={{ xs: 12, sm: 1 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Tooltip title={t('timekeeper.employees.dialog.assign_position.confirm_dates')} arrow>
                                        <IconButton size="small" color="primary" onClick={() => onToggleEdit(row.local_id)}>
                                            <CheckOutlinedIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            )}
                        </Grid>
                    </LocalizationProvider>
                )}

                {dateErrors.length > 0 && (
                    <Stack spacing={0.25} sx={{ mt: 1 }}>
                        {dateErrors.map((err, i) => (
                            <Typography key={i} variant="caption" color="error" sx={{ display: 'block' }}>
                                {'\u2022'} {err}
                            </Typography>
                        ))}
                    </Stack>
                )}
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 1.5, pt: 0, flexWrap: 'wrap', gap: 0.5 }}>
                {row.to_delete ? (
                    <Tooltip title={t('timekeeper.employees.dialog.assign_position.buttons.restore', 'Deshacer eliminación')} arrow>
                        <IconButton size="small" color="warning" onClick={() => onRestore(row.local_id)}>
                            <RestoreFromTrashIcon />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <IconButton size="small" color="error" onClick={() => onRemove(row.local_id)}>
                        <DeleteOutlineIcon />
                    </IconButton>
                )}
            </CardActions>
        </Card>
    );
};

// Paleta de colores que se cicla por tramo para diferenciar visualmente cada posición asignada.
const SEGMENT_PALETTE = ['#7c3aed', '#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6', '#a855f7'];

const HorizontalTimeline = ({ rows, contractStart, contractEnd }: { rows: PositionRow[]; contractStart: Dayjs | null; contractEnd: Dayjs | null }) => {
    const { t } = useTranslation();

    if (!contractStart || !contractEnd) {
        return (
            <Box sx={{ px: 1, py: 1.5, color: 'text.secondary', textAlign: 'center' }}>
                <Typography variant="caption">{t('timekeeper.employees.dialog.assign_position.messages.select_contract_first')}</Typography>
            </Box>
        );
    }

    const totalDays = Math.max(1, contractEnd.diff(contractStart, 'day'));
    const percent = (d: Dayjs | null) => {
        if (!d) return 0;
        const days = d.diff(contractStart, 'day');
        return Math.min(100, Math.max(0, (days / totalDays) * 100));
    };

    return (
        <Stack spacing={1} sx={{ width: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
                <Stack alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
                    <FlagCircleIcon fontSize="small" color="inherit" />
                    <Typography variant="caption" fontWeight={600} noWrap>
                        {contractStart.format('DD/MM/YYYY')}
                    </Typography>
                </Stack>

                {/* Riel horizontal del rango del contrato */}
                <Box sx={{ position: 'relative', flex: 1, height: 6, borderRadius: 3, bgcolor: 'action.hover', minWidth: 100 }}>
                    {rows.map((row, i) => {
                        const color = SEGMENT_PALETTE[i % SEGMENT_PALETTE.length];
                        const left = percent(row.start_date);
                        const right = percent(row.end_date);
                        const width = Math.max(1, right - left);
                        const tooltipTitle = `${row.work_position_name} · ${row.start_date?.format('DD/MM/YYYY') ?? '—'} → ${row.end_date?.format('DD/MM/YYYY') ?? '—'}`;
                        return (
                            <Tooltip key={row.local_id} title={tooltipTitle} placement="top" arrow>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: -3,
                                        left: `${left}%`,
                                        width: `${width}%`,
                                        height: 12,
                                        borderRadius: 3,
                                        bgcolor: color,
                                        border: (theme) => `1px solid ${theme.palette.background.paper}`,
                                        boxShadow: `0 0 0 1px ${color}55`,
                                        cursor: 'pointer',
                                    }}
                                />
                            </Tooltip>
                        );
                    })}
                </Box>

                <Stack alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
                    <SportsScoreIcon fontSize="small" color="inherit" />
                    <Typography variant="caption" fontWeight={600} noWrap>
                        {contractEnd.format('DD/MM/YYYY')}
                    </Typography>
                </Stack>
            </Stack>

            {rows.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ pl: 0.5 }}>
                    {rows.map((row, i) => {
                        const color = SEGMENT_PALETTE[i % SEGMENT_PALETTE.length];
                        return (
                            <Tooltip key={row.local_id} title={row.work_position_name} placement="top" arrow>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                                    <Typography variant="caption" noWrap sx={{ maxWidth: 120 }}>
                                        {row.work_position_name}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Stack>
            )}
        </Stack>
    );
};

// Modal de selección de puesto con árbol virtualizado (react-window) y buscador integrado.
const OrgPathPickerModal = ({
    open,
    onClose,
    onAssign,
    subcompanyTree,
    assignedWorkPositionIds,
    targetRow,
}: {
    open: boolean;
    onClose: () => void;
    onAssign: (wp: WorkPositionTreeNode) => void;
    subcompanyTree: { subcompany: Subcompany; rootAreas: AreaTreeNode[] }[];
    /** Path-based ids ("sub-1/area-5/.../wp-12") de los puestos ya asignados. */
    assignedWorkPositionIds: Set<string>;
    targetRow?: PositionRow;
}) => {
    const { t } = useTranslation();
    const apiRef = useRef<VirtualTreeApiRef>(null);
    const [pendingWP, setPendingWP] = useState<WorkPositionTreeNode | null>(null);
    const [pendingItemId, setPendingItemId] = useState<string | null>(null);

    // Ancestors del puesto a editar; el árbol monta ya con este path expandido
    const initialExpandedIds = useMemo<Set<string> | undefined>(() => {
        if (!targetRow?.work_position_id || !targetRow.area_ids.length) return undefined;
        const parts = [`sub-${targetRow.subcompany_id}`, ...targetRow.area_ids.map((id) => `area-${id}`), `wp-${targetRow.work_position_id}`];
        const set = new Set<string>();
        for (let i = 1; i < parts.length; i++) set.add(parts.slice(0, i).join('/'));
        return set;
    }, [targetRow]);

    useEffect(() => {
        if (!open) return;
        if (targetRow?.work_position_id && targetRow.area_ids.length > 0) {
            const wpId = [`sub-${targetRow.subcompany_id}`, ...targetRow.area_ids.map((id) => `area-${id}`), `wp-${targetRow.work_position_id}`].join('/');
            setPendingWP({ work_position_id: targetRow.work_position_id, work_position_name: targetRow.work_position_name });
            setPendingItemId(wpId);
            // El árbol ya monta con initialExpandedIds; solo se hace scroll al nodo
            apiRef.current?.focusItem(wpId);
        } else {
            setPendingWP(null);
            setPendingItemId(null);
        }
    }, [open, targetRow]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box component="span">{t('timekeeper.employees.dialog.assign_position.picker.title', 'Seleccionar puesto')}</Box>
                <Stack direction="row" spacing={0.25}>
                    <Tooltip title={t('general.actions.expand_all', 'Expandir todo')} arrow>
                        <IconButton size="small" onClick={() => apiRef.current?.expandAll()}>
                            <UnfoldMoreIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('general.actions.collapse_all', 'Colapsar todo')} arrow>
                        <IconButton size="small" onClick={() => apiRef.current?.collapseAll()}>
                            <UnfoldLessIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.5}>
                    {pendingWP && <Chip color="primary" variant="outlined" icon={<WorkOutlineIcon />} label={pendingWP.work_position_name} size="small" sx={{ alignSelf: 'flex-start' }} />}
                    <VirtualTree
                        ref={apiRef}
                        subcompanyTree={subcompanyTree}
                        onSelectWorkPosition={(wp, itemId) => {
                            setPendingWP(wp);
                            setPendingItemId(itemId);
                        }}
                        assignedWorkPositionIds={assignedWorkPositionIds}
                        selectedItemId={pendingItemId}
                        height={440}
                        initialExpandedIds={initialExpandedIds}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="error" onClick={onClose}>
                    {t('general.buttons.cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!pendingWP}
                    startIcon={<CheckOutlinedIcon />}
                    onClick={() => {
                        if (pendingWP) onAssign(pendingWP);
                    }}
                >
                    {t('general.buttons.assign')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const AssignPositionNew = ({ open, setOpen, reloading, data }: AssignPositionNewProps) => {
    const { t } = useTranslation();
    const { handlerAlert } = useContext(GlobalContext);

    // El diálogo se monta con `data` en undefined mientras no hay selección; evitamos acceder a employee_id.
    const employeeId = data?.employee_id ?? 0;

    const { data: queryData, loading: loadingQuery } = useAssignPositionQuery({
        employee_id: employeeId,
        open: open && !!data?.employee_id,
    });

    const fallbackOrgpaths = useMemo(() => {
        const anyData = data as any;
        // Recopila todas las fuentes de tabla; tienen la estructura anidada (Range/Subcompany/Area/Posicion)
        // necesaria para buildRowFromOrgpath, a diferencia del API que puede devolver registros planos.
        const fromTable: EmployeeOrgpath[] = [
            ...(Array.isArray(anyData?.org_path) ? anyData.org_path : []),
            ...(Array.isArray(anyData?.hire_date) ? anyData.hire_date : []),
            ...(Array.isArray(anyData?.orgpath) ? anyData.orgpath : []),
        ];
        // Tabla primero: al deduplicar se conserva la entrada con estructura más rica.
        const all = [...fromTable, ...queryData.orgpaths];
        if (all.length === 0) return [] as EmployeeOrgpath[];
        const seen = new Set<number | string>();
        return all.filter((op) => {
            const id = (op as any).hiring_orpath_id;
            if (id == null) return true;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });
    }, [queryData.orgpaths, data]);

    const {
        selectedHiringId,
        selectedHiring,
        rows,
        subcompanyTree,
        hasUnsavedChanges,
        isValid,
        rowDateErrors,
        preloadedCountByHiring,
        setSelectedHiringId,
        addRowFromTree,
        removeRow,
        restoreRow,
        toggleEditRow,
        updateRowFromTree,
        changeRowDate,
        reset,
    } = useAssignPositionForm({
        hirings: queryData.hiring_range,
        subcompanies: queryData.subcompanies,
        areas: queryData.areas as unknown as AreaTreeNode[],
        orgpaths: fallbackOrgpaths,
    });

    const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);
    const [orgPickerOpen, setOrgPickerOpen] = useState(false);
    const [orgPickerTargetRowId, setOrgPickerTargetRowId] = useState<string | null>(null);

    const contractStart = selectedHiring?.start_date ? dayjs(selectedHiring.start_date) : null;
    const contractEnd = selectedHiring?.end_date ? dayjs(selectedHiring.end_date) : null;

    const hasFullContractRow = useMemo(() => {
        if (!contractStart || !contractEnd) return false;
        const activeRows = rows.filter((r) => !r.to_delete && r.end_date?.isValid());
        if (activeRows.length === 0) return false;
        const maxEnd = activeRows.reduce((m, r) => (r.end_date!.isAfter(m) ? r.end_date! : m), activeRows[0].end_date!);
        // No hay espacio si el siguiente start caería en contractEnd o más allá
        return !maxEnd.isBefore(contractEnd, 'day');
    }, [rows, contractStart, contractEnd]);

    const closing = useCallback(
        (skipConfirm = false) => {
            if (!skipConfirm && hasUnsavedChanges) {
                setShowUnsavedAlert(true);
                return;
            }
            setShowUnsavedAlert(false);
            reset();
            setOpen(false);
        },
        [hasUnsavedChanges, reset, setOpen],
    );

    const { saving, saveAssignments } = useAssignPositionActions({
        employee_id: employeeId,
        onSuccess: () => closing(true),
        reloading,
        handlerAlert,
        t,
    });

    const handleOpenOrgPicker = useCallback(
        (localId: string | null) => {
            if (!selectedHiringId) {
                enqueueSnackbar(t('timekeeper.employees.dialog.assign_position.messages.select_contract_first', 'Selecciona un contrato antes de asignar puestos'), { variant: 'warning' });
                return;
            }
            setOrgPickerTargetRowId(localId);
            setOrgPickerOpen(true);
        },
        [selectedHiringId, t],
    );

    const handleOrgPickerAssign = useCallback(
        (wp: WorkPositionTreeNode) => {
            if (orgPickerTargetRowId === null) {
                const result = addRowFromTree({ workPositionId: wp.work_position_id, workPositionName: wp.work_position_name });
                if (!result.added && result.reason === 'duplicate') {
                    enqueueSnackbar(t('timekeeper.employees.dialog.assign_position.messages.duplicate', 'Este puesto ya está en la lista'), { variant: 'info' });
                }
            } else {
                updateRowFromTree(orgPickerTargetRowId, wp.work_position_id, wp.work_position_name);
            }
            setOrgPickerOpen(false);
            setOrgPickerTargetRowId(null);
        },
        [orgPickerTargetRowId, addRowFromTree, updateRowFromTree, t],
    );

    const contractOptions = queryData.hiring_range;

    // Path-based ids ("sub-1/area-5/.../wp-12") para no marcar como "Asignado" a otras ramas que compartan work_position_id.
    const assignedWorkPositionIds = useMemo(
        () =>
            new Set(
                rows
                    .filter((r) => !r.to_delete && r.work_position_id && r.area_ids.length > 0)
                    .map((r) => [`sub-${r.subcompany_id}`, ...r.area_ids.map((id) => `area-${id}`), `wp-${r.work_position_id}`].join('/')),
            ),
        [rows],
    );

    useEffect(() => {
        if (!open || !data?.employee_id || selectedHiringId === null) return;
    }, [open, data?.employee_id, selectedHiringId, rows]);

    if (!open || !data?.employee_id) return null;

    return (
        <Dialog open={open} onClose={() => closing(false)} maxWidth="md" fullWidth fullScreen={fullScreen}>
            <DialogTitle sx={{ background: (theme) => theme.palette.background.default, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1.5 }}>
                <Box component="span">{t('timekeeper.employees.dialog.assign_position.title', 'Asignar puesto')}</Box>
                <Tooltip title={fullScreen ? t('general.actions.exit_fullscreen') : t('general.actions.fullscreen')} arrow>
                    <IconButton size="small" onClick={() => setFullScreen((v) => !v)} aria-label="toggle fullscreen">
                        {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                    </IconButton>
                </Tooltip>
            </DialogTitle>

            {showUnsavedAlert && (
                <Box sx={{ top: 0, zIndex: 10, pb: 1, px: 3, pt: 2 }}>
                    <Alert
                        variant="filled"
                        severity="warning"
                        action={
                            <>
                                <Button onClick={() => setShowUnsavedAlert(false)} color="primary" size="small" variant="contained" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes', 'Continuar')}
                                </Button>
                                <Button onClick={() => closing(true)} color="error" size="small" variant="contained" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no', 'Descartar')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message', 'Hay cambios sin guardar')}</AlertTitle>
                    </Alert>
                </Box>
            )}

            <DialogContent dividers sx={{ background: (theme) => theme.palette.background.default }}>
                <Stack direction="column" spacing={2}>
                    {/* Fila 1: Selector de contrato */}
                    <Autocomplete
                        size="small"
                        options={contractOptions}
                        loading={loadingQuery}
                        getOptionLabel={(o) => {
                            const name = o.hiring?.name_hiring ?? '';
                            const start = o.start_date ? dayjs(o.start_date).format('DD/MM/YYYY') : '';
                            const end = o.end_date ? dayjs(o.end_date).format('DD/MM/YYYY') : '';
                            return `${name} · ${start} — ${end}`;
                        }}
                        isOptionEqualToValue={(o, v) => o.hiring?.hiring_id === v.hiring?.hiring_id}
                        value={contractOptions.find((c) => c.hiring?.hiring_id === selectedHiringId) ?? null}
                        onChange={(_, val) => setSelectedHiringId(val?.hiring?.hiring_id ?? null)}
                        renderOption={(props, option) => {
                            const hiringId = option.hiring?.hiring_id;
                            const count = hiringId ? (preloadedCountByHiring[hiringId] ?? 0) : 0;
                            const name = option.hiring?.name_hiring ?? '';
                            const start = option.start_date ? dayjs(option.start_date).format('DD/MM/YYYY') : '';
                            const end = option.end_date ? dayjs(option.end_date).format('DD/MM/YYYY') : '';
                            return (
                                <Box component="li" {...props} key={hiringId ?? `${name}-${start}`}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                                        <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                                            {`${name} · ${start} — ${end}`}
                                        </Typography>
                                        {count > 0 && (
                                            <Chip
                                                size="small"
                                                color="info"
                                                variant="outlined"
                                                label={t('timekeeper.employees.dialog.assign_position.contract_positions_count', '{{count}} asignado(s)', { count })}
                                            />
                                        )}
                                    </Stack>
                                </Box>
                            );
                        }}
                        renderInput={(params) => <TextField {...params} required label={t('timekeeper.employees.dialog.assign_position.contract', 'Contrato')} />}
                    />

                    {/* Fila 2: Timeline horizontal */}
                    <Box sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1.5 }}>
                        <HorizontalTimeline rows={rows.filter((r) => !r.to_delete)} contractStart={contractStart} contractEnd={contractEnd} />
                    </Box>

                    {/* Fila 3: Lista de asignaciones */}
                    <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {t('timekeeper.employees.dialog.assign_position.position_selected')}
                                </Typography>
                                <Chip label={rows.length} size="small" color={'default'} variant="outlined" />
                            </Stack>
                            <Tooltip title={hasFullContractRow ? t('timekeeper.employees.dialog.assign_position.messages.full_contract_covered') : ''} arrow>
                                <span>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="inherit"
                                        startIcon={<AddCircleOutlineIcon />}
                                        disabled={hasFullContractRow}
                                        onClick={() => handleOpenOrgPicker(null)}
                                    >
                                        {t('timekeeper.employees.dialog.assign_position.add_position')}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Stack>

                        {rows.length === 0 ? (
                            <Box
                                sx={{
                                    border: (theme) => `1px dashed ${theme.palette.divider}`,
                                    borderRadius: 1,
                                    p: 4,
                                    textAlign: 'center',
                                    color: 'text.secondary',
                                }}
                            >
                                <Typography variant="body2">{t('timekeeper.employees.dialog.assign_position.empty')}</Typography>
                            </Box>
                        ) : (
                            <Box sx={{ maxHeight: fullScreen ? 'calc(100vh - 380px)' : 300, overflow: 'auto', pr: 0.5 }}>
                                {rows.map((row, index) => (
                                    <AssignmentCard
                                        key={row.local_id}
                                        row={row}
                                        index={index}
                                        onRemove={removeRow}
                                        onRestore={restoreRow}
                                        onToggleEdit={toggleEditRow}
                                        onOpenOrgPicker={handleOpenOrgPicker}
                                        onChangeDate={changeRowDate}
                                        contractStart={contractStart}
                                        contractEnd={contractEnd}
                                        dateErrors={rowDateErrors[row.local_id] ?? []}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ background: (theme) => theme.palette.background.default }}>
                <Button variant="outlined" color="error" onClick={() => closing(false)}>
                    {t('general.buttons.cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!isValid || saving}
                    loading={saving}
                    onClick={() => {
                        if (selectedHiringId !== null) {
                            saveAssignments(selectedHiringId, rows);
                        }
                    }}
                >
                    {t('general.buttons.assign')}
                </Button>
            </DialogActions>

            <OrgPathPickerModal
                open={orgPickerOpen}
                onClose={() => {
                    setOrgPickerOpen(false);
                    setOrgPickerTargetRowId(null);
                }}
                onAssign={handleOrgPickerAssign}
                subcompanyTree={subcompanyTree}
                assignedWorkPositionIds={assignedWorkPositionIds}
                targetRow={orgPickerTargetRowId ? rows.find((r) => r.local_id === orgPickerTargetRowId) : undefined}
            />
        </Dialog>
    );
};
